import {
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Text,
	Textarea,
	useToast,
	VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { routes } from "../../configs/constants/routes";
import { apiStore } from "../../stores/api.store";
import { useAuthStore } from "../../stores/auth.store";

const SSETestPage: React.FC = () => {
	const { t } = useTranslation();
	const toast = useToast();
	const authStore = useAuthStore();
	const [message, setMessage] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [sentMessages, setSentMessages] = useState<Array<{ message: string; timestamp: string }>>(
		[]
	);

	const handleSendMessage = async () => {
		if (!message.trim()) {
			toast({
				title: "Error",
				description: "Please enter a message",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		if (!authStore.orgId) {
			toast({
				title: "Error",
				description: "Organization ID not found",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		setIsSending(true);

		try {
			await apiStore.post({
				path: `/internal-notification`,
				body: {
					type: "notification",
					message: message.trim(),
				},
				isAuthRequired: true,
			});

			// Add to sent messages history
			setSentMessages([
				{
					message: message.trim(),
					timestamp: new Date().toLocaleTimeString(),
				},
				...sentMessages,
			]);

			// Clear the input
			setMessage("");

			toast({
				title: "Success",
				description: "Message sent successfully",
				status: "success",
				duration: 3000,
				isClosable: true,
			});
		} catch (error) {
			// Error sending SSE message
			toast({
				title: "Error",
				description: "Failed to send message",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
		} finally {
			setIsSending(false);
		}
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={[{ title: "SSE Test", path: routes.SSE_TEST }]} />
			<Flex direction="column" maxWidth="800px" mx="auto" p={4}>
				<Box bg="white" p={6} borderRadius="md" boxShadow="sm" mb={6}>
					<Text fontSize="xl" fontWeight="bold" mb={4}>
						SSE Notification Test
					</Text>
					<Text mb={4}>
						Use this page to send test SSE notifications to the current organization.
					</Text>

					<FormControl mb={4}>
						<FormLabel>Message</FormLabel>
						<Textarea
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder="Enter notification message"
							rows={4}
						/>
					</FormControl>

					<Button
						colorScheme="blue"
						onClick={handleSendMessage}
						isLoading={isSending}
						loadingText="Sending"
						width="full"
					>
						Send Notification
					</Button>
				</Box>

				{sentMessages.length > 0 && (
					<Box bg="white" p={6} borderRadius="md" boxShadow="sm">
						<Text fontSize="lg" fontWeight="bold" mb={4}>
							Sent Messages History
						</Text>
						<VStack spacing={3} align="stretch">
							{sentMessages.map((item, index) => (
								<Box key={index} p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200">
									<Flex justify="space-between" mb={2}>
										<Text fontWeight="medium">Message:</Text>
										<Text color="gray.500" fontSize="sm">
											{item.timestamp}
										</Text>
									</Flex>
									<Text>{item.message}</Text>
								</Box>
							))}
						</VStack>
					</Box>
				)}
			</Flex>
		</div>
	);
};

export default SSETestPage;
